/**
 * AnnexListPage — danh sách phụ lục hợp đồng. Đồng bộ ContractListView.
 */
import { type MouseEvent, useMemo, useState } from 'react'
import {
  Alert, Box, Button, CircularProgress, Divider,
  Popover, Stack, Table, TableBody, TableCell, TableHead, TablePagination, TableRow,
  TextField, Typography,
} from '@mui/material'
import { alpha, type SxProps, type Theme } from '@mui/material/styles'
import { TableVirtuoso } from 'react-virtuoso'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'
import { useNavigate } from 'react-router-dom'

import ConfirmDialog from '@/components/ConfirmDialog'
import RoleGuard from '@/components/RoleGuard'
import { useSnackbar } from '@/hooks/useSnackbar'
import { usePagePermission } from '../../hooks/usePagePermission'
import { useDeleteAnnexMutation, useLazyPrintAnnexQuery, useListAnnexesQuery } from '@/store/api/annexesApi'
import type { AnnexOut } from '@/types/api'
import { getErrorMessage } from '@/utils/error'
import { sumNumericColumn } from '@/utils/tableTotals'

import AnnexFormDialog from './AnnexFormDialog'

type FilterKind = 'search' | 'date'
const ALL_ROWS_PAGE_SIZE = 1_000_000

const filterBarBtnSx: SxProps<Theme> = (theme) => ({
  minWidth: 0, px: 0.75, py: 0.2, minHeight: 26, textTransform: 'none', fontSize: '0.7rem', lineHeight: 1.1,
  '&.MuiButton-outlined': { borderColor: alpha(theme.palette.text.primary, 0.14), '&:hover': { borderColor: alpha(theme.palette.text.primary, 0.24), backgroundColor: alpha(theme.palette.text.primary, 0.04) } },
})
const actionToolbarBtnSx: SxProps<Theme> = { minWidth: 0, px: 0.75, py: 0.2, minHeight: 26, fontSize: '0.72rem', lineHeight: 1.1, textTransform: 'none' }

type ColumnKind = 'text' | 'date' | 'number'
type ColumnDef = { key: keyof AnnexOut; label: string; kind?: ColumnKind; align?: 'right' }
const NON_SUM_TOTAL_KEYS = new Set<string>(['price'])

const TABLE_COLUMNS: ColumnDef[] = [
  { key: 'annex_no', label: 'Số phụ lục' },
  { key: 'contract_no', label: 'Số HĐ' },
  { key: 'annex_date', label: 'Ngày', kind: 'date' },
  { key: 'supplier_name', label: 'Nhà cung cấp' },
  { key: 'content_before_change', label: 'Trước thay đổi' },
  { key: 'content_after_change', label: 'Sau thay đổi' },
  { key: 'price', label: 'Giá', kind: 'number', align: 'right' },
  { key: 'quantity', label: 'SL (kg)', kind: 'number', align: 'right' },
  { key: 'note', label: 'Ghi chú' },
]

const ACCESS_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const formatAccessDate = (v: unknown): string => { if (!v) return ''; const d = new Date(String(v)); if (Number.isNaN(d.getTime())) return String(v); return `${`${d.getDate()}`.padStart(2, '0')}-${ACCESS_MONTHS[d.getMonth()]}-${`${d.getFullYear()}`}` }
const formatAccessNumber = (v: number): string => Number.isInteger(v) ? v.toLocaleString('en-US') : v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const truncate = (text: string | null | undefined, max = 80): string => !text ? '' : text.length > max ? `${text.slice(0, max)}…` : text

const rowMatchesSearch = (row: AnnexOut, term: string): boolean => {
  const needle = term.trim().toLowerCase()
  if (!needle) return true
  return TABLE_COLUMNS.some((col) => { const v = row[col.key]; return v != null && String(v).toLowerCase().includes(needle) })
}

const cellValue = (row: AnnexOut, col: ColumnDef): string => {
  const val = row[col.key]
  if (val == null) return ''
  if (col.kind === 'date') return formatAccessDate(val)
  if (col.kind === 'number' && typeof val === 'number') return formatAccessNumber(val)
  if (col.key === 'content_before_change' || col.key === 'content_after_change') return truncate(String(val))
  if (typeof val === 'number') return formatAccessNumber(val)
  return String(val)
}

export function AnnexListPage() {
  const { canEdit } = usePagePermission()
  const snack = useSnackbar()
  const navigate = useNavigate()

  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState<number>(-1)

  const [searchTerm, setSearchTerm] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')

  const [filterPopover, setFilterPopover] = useState<{ kind: FilterKind; anchor: HTMLElement } | null>(null)
  const closeFilterPopover = () => setFilterPopover(null)
  const openFilterPopover = (kind: FilterKind, e: MouseEvent<HTMLElement>) => setFilterPopover({ kind, anchor: e.currentTarget })

  const hasActiveFilters = Boolean(searchTerm.trim() || dateFrom || dateTo)
  const clearAllFilters = () => { setSearchTerm(''); setDateFrom(''); setDateTo(''); setPage(0) }

  const [createOpen, setCreateOpen] = useState(false)
  const [editing, setEditing] = useState<AnnexOut | null>(null)
  const [confirmDel, setConfirmDel] = useState<AnnexOut | null>(null)

  const { data, isLoading, isFetching, error } = useListAnnexesQuery({ page: 1, page_size: ALL_ROWS_PAGE_SIZE })
  const [deleteAnnex, { isLoading: deleting }] = useDeleteAnnexMutation()
  const [triggerPrint] = useLazyPrintAnnexQuery()

  const baseRows = useMemo(() => data?.items ?? [], [data])
  const filteredRows = useMemo(() =>
    baseRows.filter((row) => {
      if (!rowMatchesSearch(row, searchTerm)) return false
      if (dateFrom && (!row.annex_date || row.annex_date < dateFrom)) return false
      if (dateTo && (!row.annex_date || row.annex_date > dateTo)) return false
      return true
    }), [baseRows, searchTerm, dateFrom, dateTo])

  const rows = useMemo(() => rowsPerPage === -1 ? filteredRows : filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage), [filteredRows, page, rowsPerPage])

  const totalRowValues = useMemo(
    () =>
      new Map(
        TABLE_COLUMNS.filter((col) => col.kind === 'number' && !NON_SUM_TOTAL_KEYS.has(String(col.key))).map(
          (col) => [String(col.key), sumNumericColumn(filteredRows, col.key)],
        ),
      ),
    [filteredRows],
  )

  const gridKey = `${rowsPerPage}:${page}:${filteredRows.length}`
  const selectedRow = useMemo(() => baseRows.find((r) => r.id === selectedId) ?? null, [baseRows, selectedId])

  const handleEdit = () => { if (selectedRow) setEditing(selectedRow) }
  const handleViewContract = () => {
    if (!selectedRow?.contract_no) return
    const cn = selectedRow.contract_no
    if (cn.startsWith('NPE-')) navigate(`/purchase/npe-contracts`)
    else navigate(`/purchase/local-p/${encodeURIComponent(cn)}`)
  }
  const handlePrint = async (cancel: boolean) => {
    if (!selectedRow) return
    try { await triggerPrint({ id: selectedRow.id, cancel }).unwrap(); snack.info(`Yêu cầu in ${cancel ? 'huỷ' : 'phụ lục'} đã gửi`) }
    catch (err) { snack.error(getErrorMessage(err, 'In PDF thất bại')) }
  }
  const handleConfirmDelete = async () => {
    if (!confirmDel) return
    try { await deleteAnnex(confirmDel.id).unwrap(); snack.success(`Đã xoá ${confirmDel.annex_no}`); setConfirmDel(null) }
    catch (err) { snack.error(getErrorMessage(err, 'Xoá phụ lục thất bại')) }
  }

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <Stack direction="row" flexWrap="wrap" useFlexGap justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
        <Stack direction="row" spacing={0.375} flexWrap="wrap" useFlexGap alignItems="center" sx={{ minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mr: 0.25 }}>Filter</Typography>
          <Button size="small" variant={searchTerm.trim() ? 'contained' : 'outlined'} color={searchTerm.trim() ? 'primary' : 'inherit'} endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />} onClick={(e) => openFilterPopover('search', e)} sx={filterBarBtnSx}>Search</Button>
          <Button size="small" variant={dateFrom || dateTo ? 'contained' : 'outlined'} color={dateFrom || dateTo ? 'primary' : 'inherit'} endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />} onClick={(e) => openFilterPopover('date', e)} sx={filterBarBtnSx}>Date</Button>
          {hasActiveFilters && <Button size="small" variant="text" color="secondary" onClick={clearAllFilters} sx={filterBarBtnSx}>Clear All</Button>}
        </Stack>
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap justifyContent="flex-end" alignItems="center">
          <Button variant="contained" size="small" color="success" sx={actionToolbarBtnSx} disabled={!canEdit} onClick={() => setCreateOpen(true)}>New</Button>
          <Button variant="outlined" size="small" color="warning" sx={actionToolbarBtnSx} disabled={!selectedRow || !canEdit} onClick={handleEdit}>Edit</Button>
          <Button variant="outlined" size="small" color="error" sx={actionToolbarBtnSx} disabled={!selectedRow || !canEdit} onClick={() => selectedRow && setConfirmDel(selectedRow)}>Delete</Button>
          <Button variant="outlined" size="small" color="primary" sx={actionToolbarBtnSx} disabled={!selectedRow} onClick={handleViewContract}>View HĐ</Button>
          <Button variant="outlined" size="small" sx={{ ...actionToolbarBtnSx, color: 'primary.main', borderColor: 'primary.main' }} disabled={!selectedRow} onClick={() => void handlePrint(false)}>Print</Button>
          <Button variant="outlined" size="small" sx={{ ...actionToolbarBtnSx, color: 'primary.main', borderColor: 'primary.main' }} disabled={!selectedRow} onClick={() => void handlePrint(true)}>Print Cancel</Button>
        </Stack>
      </Stack>
      <Divider sx={{ mb: 0.5 }} />

      {error ? <Alert severity="error" sx={{ mb: 1 }}>Failed to load Annexes.</Alert> : null}

      <Box className="app-dataframe">
        <Box className="app-dataframe-toolbar" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1, flexWrap: 'wrap', minHeight: 30, py: 0.125 }}>
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ flexShrink: 0 }}>
            {(isLoading || isFetching) && <CircularProgress size={15} />}
            <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5, fontSize: '0.68rem' }}>Total: {filteredRows.length}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5, fontSize: '0.68rem' }}>Loaded: {baseRows.length}</Typography>
            <TablePagination component="div" count={filteredRows.length} page={rowsPerPage === -1 ? 0 : page} onPageChange={(_, p) => setPage(p)} rowsPerPage={rowsPerPage} onRowsPerPageChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(0) }} rowsPerPageOptions={[20, 50, 100, { label: 'All', value: -1 }]} labelRowsPerPage="Rows" sx={{ flexShrink: 0, '& .MuiTablePagination-toolbar': { minHeight: 32, px: 0.5 }, '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows, & .MuiTablePagination-select': { fontSize: '0.72rem' } }} />
          </Stack>
        </Box>

        <Popover open={filterPopover !== null} anchorEl={filterPopover?.anchor ?? null} onClose={closeFilterPopover} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }} transformOrigin={{ vertical: 'top', horizontal: 'left' }} PaperProps={{ sx: { p: 1.5, minWidth: 240, maxWidth: 360 } }}>
          {filterPopover?.kind === 'search' && (
            <Stack spacing={1}>
              <TextField size="small" fullWidth autoFocus label="Tìm kiếm" placeholder="Annex no, contract no, supplier..." value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setPage(0) }} />
              <Button size="small" variant="text" onClick={() => { setSearchTerm(''); setPage(0); closeFilterPopover() }}>Clear Search</Button>
            </Stack>
          )}
          {filterPopover?.kind === 'date' && (
            <Stack spacing={1} sx={{ minWidth: 240 }}>
              <DatePicker label="Từ ngày" format="DD-MMM-YYYY" value={dateFrom ? dayjs(dateFrom) : null} onChange={(d) => { setDateFrom(d ? d.format('YYYY-MM-DD') : ''); setPage(0) }} slotProps={{ textField: { size: 'small', fullWidth: true } }} />
              <DatePicker label="Đến ngày" format="DD-MMM-YYYY" value={dateTo ? dayjs(dateTo) : null} onChange={(d) => { setDateTo(d ? d.format('YYYY-MM-DD') : ''); setPage(0) }} slotProps={{ textField: { size: 'small', fullWidth: true } }} />
              <Button size="small" variant="text" onClick={() => { setDateFrom(''); setDateTo(''); setPage(0) }}>Clear Date</Button>
            </Stack>
          )}
        </Popover>

        <Box sx={{ flex: 1, minHeight: 320, overflow: 'hidden' }}>
          <TableVirtuoso
            key={gridKey}
            style={{ height: '100%' }}
            data={rows}
            fixedHeaderContent={() => (
              <TableRow>
                {TABLE_COLUMNS.map((col) => (
                  <TableCell key={String(col.key)} align={col.align} sx={{ fontWeight: 700, whiteSpace: 'nowrap', bgcolor: 'background.default' }}>{col.label}</TableCell>
                ))}
              </TableRow>
            )}
            fixedFooterContent={() => (
              <TableRow>
                {TABLE_COLUMNS.map((col, index) => {
                  const total = totalRowValues.get(String(col.key))
                  const val = total == null ? '' : formatAccessNumber(total)
                  return (
                    <TableCell
                      key={String(col.key)}
                      align={col.align}
                      sx={{
                        fontWeight: 700,
                        whiteSpace: 'nowrap',
                        py: 0.5,
                        bgcolor: 'background.paper',
                        borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                      }}
                    >
                      {index === 0 ? 'Total' : val}
                    </TableCell>
                  )
                })}
              </TableRow>
            )}
            itemContent={(_: number, row: AnnexOut) =>
              TABLE_COLUMNS.map((col) => {
                const val = cellValue(row, col)
                return (
                  <TableCell key={String(col.key)} align={col.align} sx={{ whiteSpace: 'nowrap', py: 0.5 }}>
                    <Box component="span" title={val || '-'} sx={{ display: 'inline-block', width: '100%' }}>{val || '-'}</Box>
                  </TableCell>
                )
              })
            }
            components={{
              Table: (props: any) => <Table {...props} size="small" stickyHeader sx={{ minWidth: 1700, width: '100%' }} />,
              TableHead: ((props: any) => <TableHead {...props} component="thead" />) as any,
              TableBody: ((props: any) => <TableBody {...props} component="tbody" />) as any,
              TableRow: (props: any) => {
                const idx = Number((props as any)['data-item-index'])
                const id = Number.isFinite(idx) ? rows[idx]?.id : null
                return <TableRow {...props} hover selected={id != null && id === selectedId} sx={{ cursor: 'pointer' }} onClick={() => { if (id != null) setSelectedId(id) }} />
              },
            }}
          />
        </Box>
      </Box>

      <AnnexFormDialog open={createOpen} mode="create" onClose={() => setCreateOpen(false)} />
      <AnnexFormDialog open={!!editing} mode="edit" initialValue={editing} onClose={() => setEditing(null)} />
      <ConfirmDialog open={!!confirmDel} title="Xoá phụ lục" message={confirmDel ? `Xoá phụ lục ${confirmDel.annex_no} của ${confirmDel.contract_no}?` : ''} destructive loading={deleting} onConfirm={() => void handleConfirmDelete()} onCancel={() => setConfirmDel(null)} />
    </Box>
  )
}

export default AnnexListPage
